import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { adminNudgePreview, adminNudgeSend } from "../lib/api";

const _skillFromGapName = (name) => {
  // Gap names look like "Transferable skill — Collaboration" or
  // "Learning skill — Responsibility" — strip the prefix for the nudge.
  if (!name) return "";
  const dash = name.indexOf("—");
  return dash >= 0 ? name.slice(dash + 1).trim() : name.trim();
};

export default function NudgeComposer({ open, onOpenChange, gapName }) {
  const skill = useMemo(() => _skillFromGapName(gapName), [gapName]);

  const [scope, setScope] = useState("missed_only"); // "missed_only" | "all"
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset + seed copy whenever the dialog opens for a new gap.
  useEffect(() => {
    if (!open) return;
    setSubject(`Push opportunity — ${skill}`);
    setBody(
      `Hi there,\n\n` +
        `Across all Atomic Edu teachers we're seeing ${skill} come up rarely in the activities being generated. ` +
        `It's a great fit to work in this week — try building a quick lesson, station, or rich task around it.\n\n` +
        `If you'd like a starter, hit Generate and pick ${skill} from the skill picker. ` +
        `We'll do the heavy lifting.\n\n` +
        `— Atomic Edu`
    );
    setScope("missed_only");
    setPreviewData(null);
  }, [open, skill]);

  // Auto-refresh preview when scope changes.
  useEffect(() => {
    if (!open || !skill) return;
    let abort = false;
    setPreviewLoading(true);
    adminNudgePreview({ skill, scope })
      .then((d) => {
        if (!abort) setPreviewData(d);
      })
      .catch(() => {
        if (!abort) toast.error("Couldn't load recipient preview.");
      })
      .finally(() => {
        if (!abort) setPreviewLoading(false);
      });
    return () => {
      abort = true;
    };
  }, [open, skill, scope]);

  const recipientCount = previewData?.recipient_count ?? 0;
  const integrationOk = previewData?.email_integration?.configured !== false;

  const send = async ({ dry }) => {
    setSending(true);
    try {
      const res = await adminNudgeSend({
        skill,
        scope,
        subject,
        body_text: body,
        dry_run: dry,
      });
      if (res.status === "dry_run") {
        toast.success(`Dry-run OK — would send to ${res.recipient_count} teacher(s).`);
      } else {
        toast.success(`Sent ${res.delivered_count}/${res.recipient_count} nudges.`);
        if (res.error_count > 0) {
          toast.warning(`${res.error_count} delivery error(s) — see admin nudge history.`);
        }
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the nudge.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]" data-testid="nudge-composer">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-[#1c2d3d] flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#4a6984]" /> Push nudge — {skill || "skill"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#4a6984]/75">
            Email Atomic Edu teachers a friendly note about this under-used skill.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scope toggle */}
          <div>
            <Label className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984] mb-2 block">
              Who gets this
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScope("missed_only")}
                data-testid="nudge-scope-missed"
                className={`flex-1 px-3 py-2 rounded-md text-xs font-extrabold border transition text-left ${
                  scope === "missed_only"
                    ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
                    : "bg-white text-[#4a6984] border-[#4a6984]/20 hover:bg-[#f4f5f7]"
                }`}
              >
                Teachers who HAVEN'T touched this skill recently
                <span className="block text-[10px] font-medium mt-0.5 opacity-80">
                  Skips anyone who already used it in the last 60 days.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setScope("all")}
                data-testid="nudge-scope-all"
                className={`flex-1 px-3 py-2 rounded-md text-xs font-extrabold border transition text-left ${
                  scope === "all"
                    ? "bg-[#1c2d3d] text-white border-[#1c2d3d]"
                    : "bg-white text-[#4a6984] border-[#4a6984]/20 hover:bg-[#f4f5f7]"
                }`}
              >
                Every teacher
                <span className="block text-[10px] font-medium mt-0.5 opacity-80">
                  No filtering. Use sparingly.
                </span>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-md bg-[#f4f5f7] border border-[#4a6984]/15 px-3 py-2 text-xs text-[#4a6984]">
            {previewLoading ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading recipients…
              </span>
            ) : (
              <span>
                Will send to <strong data-testid="nudge-recipient-count">{recipientCount}</strong>{" "}
                teacher{recipientCount === 1 ? "" : "s"}.{" "}
                {scope === "missed_only" && previewData?.excluded_recently_taught?.length > 0 && (
                  <span className="text-[#4a6984]/65">
                    ({previewData.excluded_recently_taught.length} skipped — already covered this skill)
                  </span>
                )}
                {!integrationOk && (
                  <span className="block mt-1 text-[#b75d2c] font-bold">
                    Resend isn't configured — only dry-runs will work.
                  </span>
                )}
              </span>
            )}
          </div>

          <div>
            <Label className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984] mb-2 block">
              Subject
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="nudge-subject"
              className="bg-white"
            />
          </div>

          <div>
            <Label className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984] mb-2 block">
              Body (plain text)
            </Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              data-testid="nudge-body"
              className="bg-white font-sans"
            />
            <p className="text-[11px] text-[#4a6984]/60 mt-1.5">
              We'll wrap this in a branded HTML email automatically.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-[#4a6984]/10">
            <Button
              variant="outline"
              onClick={() => send({ dry: true })}
              disabled={sending || !subject || !body}
              data-testid="nudge-dry-run"
              className="h-10 text-xs font-extrabold"
            >
              Dry run
            </Button>
            <Button
              onClick={() => send({ dry: false })}
              disabled={sending || !subject || !body || recipientCount === 0 || !integrationOk}
              data-testid="nudge-send"
              className="h-10 bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 text-xs font-extrabold"
            >
              {sending ? (
                <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Sending…</>
              ) : (
                <><Send className="h-3 w-3 mr-1.5" /> Send to {recipientCount}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
